import { useQuery } from '@apollo/client';
import { Campaign } from '@mui/icons-material';
import { Alert, AlertTitle, Button, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SEVERITY_COLOR, SeverityChip } from '~/components/announcements/SeverityChip';
import { useAppContext } from '~/contexts';
import { useLink } from '~/hooks/link';
import { DashboardAnnouncementAlertsDocument } from '../queries/announcements.generated';

const unreadPath = (severities?: string[]) =>
  severities
    ? `/announcements?read=unread&severities=${encodeURIComponent(JSON.stringify(severities))}`
    : '/announcements?read=unread';

// A sticky announcement gets a banner of its own, because reading never settles it. Everything
// else unread is one grey summary — anything that has to shout is sticky by then.
export const AnnouncementAlerts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const link = useLink();

  const { announcementCount } = useAppContext();

  const { data } = useQuery(DashboardAnnouncementAlertsDocument, {
    fetchPolicy: 'cache-and-network',
    skip: announcementCount === 0,
  });

  const sticky = data?.announcements.nodes ?? [];
  const unread = data?.currentUser?.unreadAnnouncementsBySeverity ?? [];
  // The badge's own field rather than a sum of the chips, so the two can never disagree.
  const unreadTotal = data?.currentUser?.unreadAnnouncementCount ?? 0;

  return (
    <>
      {sticky.map((announcement) => (
        <Alert
          key={announcement.id}
          severity={SEVERITY_COLOR[announcement.severity]}
          // Alert pulls its action 8px past its own padding; the card below does not.
          sx={{ mb: 2, '& .MuiAlert-action': { mr: 0 } }}
          action={
            <Button
              color="inherit"
              size="small"
              // Or Button's 64px minimum pads the shorter label out and centers it.
              sx={{ minWidth: 0 }}
              onClick={() => navigate(link(`/announcements/${announcement.id}`))}
            >
              {t('pages.dashboard.announcements.view')}
            </Button>
          }
        >
          <AlertTitle color="inherit" sx={{ mb: 0 }}>
            {announcement.title}
          </AlertTitle>
        </Alert>
      ))}

      {unreadTotal > 0 && (
        <Paper
          sx={{
            p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1.5,
            borderLeft: '5px solid',
            borderColor: 'primary.main',
          }}
        >
          <Campaign sx={{ color: 'text.secondary' }} />
          <Typography component="p" variant="h5" fontWeight={500} color="text.secondary">
            {t('pages.dashboard.announcements.unread_plural', { count: unreadTotal })}
          </Typography>
          {/* With one severity the chip only repeats the total. */}
          {unread.length > 1 &&
            unread.map(({ count, severity }) => (
              <SeverityChip
                key={severity}
                severity={severity}
                count={count}
                onClick={() => navigate(link(unreadPath([severity])))}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          <Button size="small" sx={{ ml: 'auto' }} onClick={() => navigate(link(unreadPath()))}>
            {t('pages.dashboard.view_all')}
          </Button>
        </Paper>
      )}
    </>
  );
};
